import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import NotFound from './NotFound';
import ProfileView from './ProfileView';
import Footer from './Footer';
import { pageColor, themeColor } from '../utils/profileStyle';

// What client/public/index.html's <meta name="theme-color"> and <title> say
// everywhere else
const APP_THEME_COLOR = '#0E0D14';
const APP_TITLE = 'Share all your links with one link. | Link Piyanos';

// Sets the tab title and returns how to put the old one back. When the old
// one is this same title (the server already put it there), "back" means the
// app's own title rather than this page's.
const setTitle = (title) => {
  const previous = document.title === title ? APP_TITLE : document.title;
  document.title = title;
  return () => { document.title = previous; };
};

/*
  src/profileSeo.js embeds the profile it has just rendered as
  window.__PROFILE__ = { username, user } (user null for an unknown handle),
  so a page opened from a link renders straight away, identical to the
  server's first paint, with no request and no loading state in between.
  It is taken once: a later client-side visit fetches fresh data.
*/
const takePreloaded = (username) => {
  const preload = window.__PROFILE__;
  if (!preload || String(preload.username).toLowerCase() !== String(username).toLowerCase()) return undefined;
  delete window.__PROFILE__;
  return preload.user;
};

const initialState = (username) => {
  const user = takePreloaded(username);
  if (user === undefined) return { username, status: 'loading' };
  return user ? { username, status: 'ready', user } : { username, status: 'missing' };
};

const recordView = (username) => axios.post(`/users/${username}/view`).catch(() => {});

// Same title the server gives the page (buildHead in src/profileSeo.js)
const titleFor = (user) => (user.name && user.name.trim() ? `${user.name.trim()} (@${user.username})` : `@${user.username}`) + ' | Piyanos Link';

/*
  Only reached without a preload (client-side navigation, or the server could
  not render the page). Nothing is known about the profile's colours yet, so
  this stays neutral: faint shapes where the page will be, which only fade in
  if the request is slow enough to need them.
*/
const ProfileLoading = () => {
  const [t] = useTranslation();
  return (
    <div className="profile-loading" role="status" aria-label={t('profileLoading')}>
      <span className="profile-loading__avatar" />
      <span className="profile-loading__line" />
      <span className="profile-loading__line profile-loading__line--short" />
      <span className="profile-loading__button" />
      <span className="profile-loading__button" />
      <span className="profile-loading__button" />
    </div>
  );
};

const UserLinkTree = ({ match }) => {
  const username = match.params.username;
  const [t] = useTranslation();
  const [state, setState] = useState(() => initialState(username));
  const user = state.status === 'ready' ? state.user : null;

  useEffect(() => {
    // Already rendered from the server's copy: only the visit is left to record
    if (state.username === username && state.status !== 'loading') {
      if (state.user) recordView(username);
      return undefined;
    }

    let current = true;
    setState({ username, status: 'loading' });
    axios.get(`/users/${username}`)
      .then(res => {
        if (!current) return;
        // null for an unknown handle; anything not an object (an HTML error
        // page from a proxy, say) is no profile either
        if (!res.data || typeof res.data !== 'object') return setState({ username, status: 'missing' });
        setState({ username, status: 'ready', user: res.data });
        recordView(username);
      })
      .catch(() => current && setState({ username, status: 'missing' }));
    return () => { current = false; };
    // Runs per address; `state` is read only to skip a fetch the preload made unnecessary
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  // The page behind the profile (seen on an overscroll), the browser's own
  // chrome and the tab title follow the profile, as the server-rendered page
  // already does; all three go back to the app's when it unmounts, so the
  // footer's link to /admin does not keep "Name (@user)" in the tab.
  useEffect(() => {
    if (!user) return undefined;
    const root = document.documentElement;
    const meta = document.querySelector('meta[name="theme-color"]');
    root.style.backgroundColor = pageColor(user.design);
    if (meta) meta.setAttribute('content', themeColor(user.design));
    const restoreTitle = setTitle(titleFor(user));
    return () => {
      root.style.backgroundColor = '';
      if (meta) meta.setAttribute('content', APP_THEME_COLOR);
      restoreTitle();
    };
  }, [user]);

  useEffect(() => {
    if (state.status !== 'missing') return undefined;
    return setTitle(`${t('profileNotExist')} | Piyanos Link`);
  }, [state.status, t]);

  const trackClick = (linkId) => {
    axios.post(`/users/${user.username}/links/${linkId}/click`).catch(() => {});
  }

  if (state.status === 'missing') return <NotFound />;
  if (!user) return <ProfileLoading />;

  return (
    <ProfileView user={user} className="profile-page-full" onLinkClick={trackClick}>
      <Footer />
    </ProfileView>
  )
}

export default UserLinkTree
